import { queryOptions, useQuery } from '@tanstack/react-query'
import { createApiClient } from '../api/helpers.ts'
import { dayjs } from '../dayjs.ts'
import { env } from '../env.ts'
import { isGroundCast, SIGNAL_THRESHOLDS } from '../features/serviceManagement/serviceMap/healthThresholds.ts'
import type { SearchDevice } from './devices.ts'

const MetricsApi = createApiClient({ prefixUrl: env.API_DEVICE_METRICS, timeout: 20_000 })

export const deviceMetricsForDeviceQuery = (gatewayId: string) =>
	queryOptions({
		queryKey: ['DEVICE_METRICS', gatewayId],
		queryFn: () => {
			return MetricsApi.get('v1/device-metrics/latest/by-gateway-id', {
				searchParams: { gateway_ids: gatewayId, lookback_hours: 4320, include_summaries: true },
			}).json<DeviceMetricsResponse>()
		},
		staleTime: 1000 * 60 * 5,
	})

export const useDeviceMetricsForDevice = (gatewayId: string) => {
	return useQuery(deviceMetricsForDeviceQuery(gatewayId))
}

/** Old FW: 1.x, 2.x, 3.0.x. Unknown formats treated as old. */
export const isOldFirmware = (firmware?: string): boolean => {
	if (!firmware) return true
	const match = /^(\d+)\.(\d+)\.(\d+)/.exec(firmware)
	if (!match) return true
	const [, majorStr, minorStr] = match
	const major = Number.parseInt(majorStr ?? '0', 10)
	const minor = Number.parseInt(minorStr ?? '0', 10)
	if (major < 3) return true
	if (major === 3 && minor === 0) return true
	return false
}

export const useSignalTestMetrics = (device: SearchDevice) => {
	return useQuery(
		queryOptions({
			queryKey: ['SIGNAL_TEST_METRICS', device.id],
			queryFn: () => {
				const endTime = dayjs().toISOString()
				const startTime = dayjs().subtract(5, 'years').toISOString()
				return MetricsApi.get('v1/device-metrics/history/by-gateway-id', {
					searchParams: {
						gateway_ids: device.id,
						tags: 'test_type:signal',
						start_time: startTime,
						end_time: endTime,
						interval: 'PT0S',
					},
				})
					.json<SignalTestResponse>()
					.then<SignalTest[]>((data) => {
						const items = data?.[device.id] ?? []
						const rssLimit = isGroundCast(device) ? SIGNAL_THRESHOLDS.rssMinGroundCast : SIGNAL_THRESHOLDS.rssMin
						const lqLimit = isOldFirmware(device.firmware) ? SIGNAL_THRESHOLDS.lqMinOldFirmware : SIGNAL_THRESHOLDS.lqMinNewFirmware
						return items
							.filter((item) => Object.keys(item).length > 1 && item['cast.test.id'] !== undefined && item['cast.test.id'] !== 0)
							.sort((a, b) => b.timestamp - a.timestamp)
							.map((item) => {
								const rss = item['modem.radioSignalStrength']
								const snr = item['modem.snr']
								const lq = item['modem.nbiot.nrsrq']
								const status = item['cast.test.status']
								const cellId = item['modem.cellId']
								return {
									timestamp: item.timestamp,
									rss,
									snr,
									lq,
									status,
									cellId: cellId === undefined ? undefined : Math.floor((cellId - 3) / 256),
									numSamples: item['cast.test.numSamples'],
									latitude: item['cast.test.geo.latitude'],
									longitude: item['cast.test.geo.longitude'],
									results: {
										success: status === 0,
										poorRss: below(rss, rssLimit),
										poorSnr: below(snr, SIGNAL_THRESHOLDS.snrMin),
										poorLq: below(lq, lqLimit),
									},
								}
							})
					})
			},
			staleTime: 1000 * 60 * 5,
		}),
	)
}

const below = (value: number | undefined, limit: number) => (value === undefined ? undefined : value < limit)

type LiteralUnion<T extends U, U = string> = T | (U & Record<never, never>)
type DeviceMetricName = LiteralUnion<
	| 'cast.internalBatteryVoltage'
	| 'cast.internalBatteryVoltage.last30DaysAvg'
	| 'cast.internalBatteryVoltage.last30DaysMin'
	| 'cast.internalTemperature'
	| 'bmu800.internalBatteryLevel'
	| 'modem.radioSignalStrength'
	| 'modem.radioSignalStrength.last24HoursAvg'
	| 'modem.radioSignalStrength.last30DaysAvg'
	| 'modem.radioSignalStrength.lifetimeAvg'
	| 'modem.snr'
	| 'modem.snr.last24HoursAvg'
	| 'modem.snr.last30DaysAvg'
	| 'modem.snr.lifetimeAvg'
	| 'modem.activeTime'
	| 'modem.activeTime.lifetimeSum'
	| 'modem.band'
	| 'modem.cellId'
	| 'modem.nbiot.nrsrq'
	| 'modem.nbiot.nrsrq.last24HoursAvg'
	| 'modem.nbiot.nrsrq.last30DaysAvg'
	| 'modem.nbiot.nrsrq.lifetimeAvg'
	| 'cast.rebootReason'
	| 'cast.anjay.retransmissionCount'
	| 'modem.cellSwitchCounter.lifetimeSum'
	| 'cast.test.id'
	| 'cast.test.status'
	| 'cast.test.geo.latitude'
	| 'cast.test.geo.longitude'
	| 'cast.test.numSamples'
>

export type DeviceMetricsResponse = Record<string, Partial<Record<DeviceMetricName, { value: number; timestamp: string }>>>

type SignalTestResponse = Record<string, Array<{ timestamp: number } & Partial<Record<DeviceMetricName, number>>>>
export type SignalTest = {
	timestamp: number
	rss?: number
	snr?: number
	lq?: number
	status?: number
	cellId?: number
	numSamples?: number
	latitude?: number
	longitude?: number
	results: {
		success: boolean
		poorRss?: boolean
		poorSnr?: boolean
		poorLq?: boolean
	}
}

export type DeviceMetric = {
	id: string
	source: string
	timestamp: string
	metric: string
	value: number
	tags?: string[]
	gatewayId?: string
}
export type DeviceMetricRecord = Record<string, Partial<Record<DeviceMetricName, DeviceMetric>>>
